import { httpClient } from "../http-client";
import api from "./api";
import { buildQueryString } from "./query-builder";
import { ApiResponse } from "../../types/responses/api-response";
import {
  EventTask,
  CreateEventTaskRequest,
  UpdateEventTaskRequest,
  TaskFilters,
} from "../../types/events-unified";

type EventTaskStatus = EventTask["status"];
type EventTaskPriority = EventTask["priority"];

export interface EventTaskFilters {
  search?: string;
  status?: EventTaskStatus;
  priority?: EventTaskPriority;
  assigneeId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  page?: number;
  limit?: number;
}

export interface ExportTasksOptions {
  // BE `exportTasks` поддерживает только csv/xlsx (excel); pdf-ветки нет
  // (event-tasks.service.ts: `isXlsx = format==='xlsx'||'excel'`, иначе csv).
  format?: "csv" | "excel";
  status?: EventTaskStatus;
  priority?: EventTaskPriority;
  assigneeId?: string;
}

/**
 * AD-662. Сколько задач сервер принимает в одной массовой правке: `taskIds` длиннее
 * получает 400 (`EVENT_TASKS_BULK_UPDATE_MAX`, BE `bulk-update-event-tasks.dto.ts`).
 * Число меняется вместе с серверным.
 */
const EVENT_TASKS_BULK_UPDATE_MAX = 100;

// ===== EVENT TASKS API =====

export interface EventTaskComment {
  id: string;
  taskId: string;
  userId: string;
  userName: string;
  userRole: string;
  content: string;
  createdAt: string;
}

export interface EventTaskStats {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  overdueTasks: number;
  completionRate: number;
  averageProgress: number;
  personalTasks: number;
  organizationalTasks: number;
  administrativeTasks: number;
}

/**
 * Ответ GET /events/:eventId/tasks на ПРОВОДЕ.
 *
 * Контроллер (`event-tasks.controller.ts`, `toItemsEnvelope`) возвращает ровно
 * `{ items, total }` — без `page` и `limit`. Глобальный `ResponseInterceptor`
 * разворачивает это в `{ success, data: items, meta: { ...serviceMeta, total } }`.
 * То есть на проводе есть ПЛОСКИЙ `meta.total` — и больше ничего о пагинации.
 *
 * Почему это отдельный тип, а не `ApiResponse<EventTask[]>`. В `ApiResponse.meta`
 * объявлено поле `pagination` — оно действительно приходит с ЛЕГАСИ-маршрутов,
 * которые оборачивают ответ руками. Для этого маршрута его нет никогда, но
 * `tasksResponse.meta.pagination` компилировался и молча давал `undefined`:
 * счётчик задач схлопывался до длины текущей страницы, а пагинация показывала
 * одну страницу при любом объёме (родня находки Н-9). Тип, который умеет сказать
 * «нет», превращает такое чтение в ошибку компиляции.
 *
 * Номер страницы сервер НЕ возвращает — он остаётся состоянием клиента.
 */
export interface EventTasksPageResponse {
  data: EventTask[];
  success?: boolean;
  message?: string;
  meta?: {
    total?: number;
    timestamp?: string;
    requestId?: string;
  };
}

// Enhanced Event Tasks API methods
export const eventTasksApi = {
  async getEventTasks(
    eventId: string,
    filters?: EventTaskFilters,
  ): Promise<EventTasksPageResponse> {
    const queryString = buildQueryString(filters || {});
    return httpClient.get<EventTask[]>(
      `/events/${eventId}/tasks${queryString}`,
    );
  },

  // Получить задачи пользователя (личные + назначенные)
  getUserTasks: (params?: TaskFilters): Promise<ApiResponse<EventTask[]>> => {
    return httpClient.get<EventTask[]>(
      `/tasks${buildQueryString(params || {})}`,
    );
  },

  // Получить задачи, созданные пользователем
  getCreatedTasks: (
    params?: TaskFilters,
  ): Promise<ApiResponse<EventTask[]>> => {
    const { page, limit, ...rest } = params || {};
    const skip = page && limit ? (page - 1) * limit : undefined;
    const take = limit;
    return httpClient.get<EventTask[]>(
      `/tasks/created${buildQueryString({ ...rest, skip, take })}`,
    );
  },

  // Получить задачи, назначенные пользователю
  getAssignedTasks: (
    params?: TaskFilters,
  ): Promise<ApiResponse<EventTask[]>> => {
    const { page, limit, ...rest } = params || {};
    const skip = page && limit ? (page - 1) * limit : undefined;
    const take = limit;
    return httpClient.get<EventTask[]>(
      `/tasks/assigned${buildQueryString({ ...rest, skip, take })}`,
    );
  },

  async getEventTask(eventId: string, taskId: string) {
    return httpClient.get<EventTask>(`/events/${eventId}/tasks/${taskId}`);
  },

  async createEventTask(
    eventId: string,
    task: CreateEventTaskRequest,
  ): Promise<ApiResponse<EventTask>> {
    return httpClient.post<EventTask>(`/events/${eventId}/tasks`, task);
  },

  async updateEventTask(
    eventId: string,
    taskId: string,
    task: UpdateEventTaskRequest,
  ): Promise<ApiResponse<EventTask>> {
    return httpClient.put<EventTask>(
      `/events/${eventId}/tasks/${taskId}`,
      task,
    );
  },

  async deleteEventTask(
    eventId: string,
    taskId: string,
  ): Promise<ApiResponse<{ success: boolean }>> {
    return httpClient.delete<{ success: boolean }>(
      `/events/${eventId}/tasks/${taskId}`,
    );
  },

  async updateEventTaskStatus(
    eventId: string,
    taskId: string,
    status: EventTaskStatus,
  ): Promise<ApiResponse<EventTask>> {
    return httpClient.patch<EventTask>(
      `/events/${eventId}/tasks/${taskId}/status`,
      { status },
    );
  },

  // Назначить исполнителя
  assignEventTask: (
    eventId: string,
    taskId: string,
    assigneeId: string,
  ): Promise<ApiResponse<EventTask>> => {
    return httpClient.patch<EventTask>(
      `/events/${eventId}/tasks/${taskId}/assign`,
      { assigneeId },
    );
  },

  async getEventTaskComments(
    eventId: string,
    taskId: string,
  ): Promise<ApiResponse<EventTaskComment[]>> {
    return httpClient.get<EventTaskComment[]>(
      `/events/${eventId}/tasks/${taskId}/comments`,
    );
  },

  async addEventTaskComment(eventId: string, taskId: string, comment: string) {
    return httpClient.post<{ id: string }>(
      `/events/${eventId}/tasks/${taskId}/comments`,
      { content: comment },
    );
  },

  async getEventTaskStats(eventId: string) {
    return httpClient.get<{
      totalTasks: number;
      completedTasks: number;
      inProgressTasks: number;
      overdueTasks: number;
      completionRate: number;
    }>(`/events/${eventId}/tasks/stats`);
  },

  // Получить статистику задач пользователя (Backend: GET /tasks/my → канон-конверт
  // ADR-TASKS: data = массив задач, meta.total = общее число).
  getUserTaskStats: async (): Promise<ApiResponse<EventTaskStats>> => {
    const empty: EventTaskStats = {
      totalTasks: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      overdueTasks: 0,
      completionRate: 0,
      averageProgress: 0,
      personalTasks: 0,
      organizationalTasks: 0,
      administrativeTasks: 0,
    };
    try {
      const res =
        await httpClient.get<
          Array<{ status?: string; deadline?: string; progress?: number }>
        >(`/tasks/my`);
      const tasks = res.data ?? [];
      const total = res.meta?.total ?? tasks.length;
      const now = new Date();
      let completedTasks = 0;
      let inProgressTasks = 0;
      let overdueTasks = 0;
      let progressSum = 0;
      for (const task of tasks) {
        if (task.status === "completed" || task.status === "COMPLETED")
          completedTasks++;
        else if (
          task.status === "in_progress" ||
          task.status === "IN_PROGRESS" ||
          task.status === "pending" ||
          task.status === "PENDING"
        )
          inProgressTasks++;
        if (
          task.deadline &&
          new Date(task.deadline) < now &&
          task.status !== "completed" &&
          task.status !== "COMPLETED"
        )
          overdueTasks++;
        progressSum += typeof task.progress === "number" ? task.progress : 0;
      }
      const averageProgress = tasks.length > 0 ? progressSum / tasks.length : 0;
      const completionRate = total > 0 ? completedTasks / total : 0;
      return {
        data: {
          totalTasks: total,
          completedTasks,
          inProgressTasks,
          overdueTasks,
          completionRate,
          averageProgress,
          personalTasks: 0,
          organizationalTasks: 0,
          administrativeTasks: 0,
        },
        success: true,
      };
    } catch (error: unknown) {
      const err = error as { response?: unknown; code?: string };
      if (
        !err.response ||
        err.code === "ECONNREFUSED" ||
        err.code === "ERR_NETWORK"
      ) {
        return { data: empty, success: true };
      }
      throw error;
    }
  },

  // AD-662. Отмеченное уходит порциями по EVENT_TASKS_BULK_UPDATE_MAX и по очереди:
  // граница сервера ограничивает работу одного запроса, параллельные порции её бы
  // обошли. Отказ на порции прерывает отправку и доходит до экрана; порции, принятые до
  // него, сервер уже применил. Пустой список уходит одним запросом, как и раньше: отказ
  // даёт сервер, успех без его ответа здесь не выдумывается.
  async bulkUpdateEventTasks(
    eventId: string,
    taskIds: string[],
    updates: Partial<UpdateEventTaskRequest>,
  ): Promise<ApiResponse<EventTask[]>> {
    const updated: EventTask[] = [];
    let start = 0;
    do {
      const response = await httpClient.post<EventTask[]>(
        `/events/${eventId}/tasks/bulk-update`,
        {
          taskIds: taskIds.slice(start, start + EVENT_TASKS_BULK_UPDATE_MAX),
          updates,
        },
      );
      updated.push(...response.data);
      start += EVENT_TASKS_BULK_UPDATE_MAX;
    } while (start < taskIds.length);
    return { success: true, data: updated };
  },

  // Экспорт задач события — BE стримит файл (blob) с Content-Disposition
  // (event-tasks.controller `res.send(buffer)`), НЕ JSON `{downloadUrl}`. Скачиваем
  // на FE через raw `api` с responseType:'blob' (httpClient-интерсепторы рассчитаны на
  // JSON ApiResponse и портят бинарный ответ — тот же приём, что exportUserTasks).
  async exportEventTasks(
    eventId: string,
    options?: ExportTasksOptions,
  ): Promise<void> {
    const format = options?.format ?? "csv";
    const queryString = buildQueryString({
      format,
      status: options?.status,
      priority: options?.priority,
      assigneeId: options?.assigneeId,
    });

    const response = await api.get(
      `/events/${eventId}/tasks/export${queryString}`,
      { responseType: "blob" },
    );
    const blob = response.data as Blob;
    const disposition = response.headers?.["content-disposition"] as
      | string
      | undefined;
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const date = new Date().toISOString().split("T")[0];
    const filename =
      match?.[1] ??
      `event-${eventId}-tasks-${date}.${format === "excel" ? "xlsx" : "csv"}`;

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  // Экспорт задач пользователя — стрим файла (blob) + скачивание на FE (T2 фаза 3).
  // Через raw `api` с responseType:'blob' (как admin.exportData) — httpClient-интерсепторы
  // рассчитаны на JSON ApiResponse и портят бинарный ответ.
  exportUserTasks: (
    format: "csv" | "excel" = "csv",
  ): Promise<{ data: Blob }> => {
    return api.get(`/tasks/export?format=${format}`, { responseType: "blob" });
  },
};
